import { useEffect, useState } from "react";
import { Mail, Send, RefreshCw, Save, Eye, AlertCircle, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";

const TEMPLATES = [
  {
    key: "birthday",
    name: "Felicitación al cliente",
    description: "Email enviado al cliente el día de su cumpleaños con cupón de descuento",
    variables: "{name} · {code} · {discount}",
    hasDiscount: true,
  },
  {
    key: "admin_birthday_notify",
    name: "Recordatorio interno (admin)",
    description: "Aviso interno 1 ó 2 días antes para preparar regalo físico",
    variables: "{name} {surname} {email} {phone} {address} {postal_code} {city} {birthdate} {days_until}",
    hasDiscount: false,
  },
];


export default function AdminEmails() {
  const [activeKey, setActiveKey] = useState("birthday");
  const [template, setTemplate] = useState(null);
  const [logs, setLogs] = useState([]);
  const [testEmail, setTestEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const loadTemplate = (key) => api.get(`/admin/email/templates/${key}`).then(r => setTemplate(r.data));
  const loadLogs = () => api.get("/admin/email/logs").then(r => setLogs(r.data || []));

  useEffect(() => { loadTemplate(activeKey); }, [activeKey]);
  useEffect(() => { loadLogs(); }, []);

  const save = async () => {
    try {
      await api.put(`/admin/email/templates/${activeKey}`, {
        subject: template.subject,
        html: template.html,
        ...(template.discount_percent !== undefined ? {discount_percent: parseInt(template.discount_percent) || 15} : {}),
        ...(template.discount_code_prefix ? {discount_code_prefix: template.discount_code_prefix} : {}),
      });
      toast.success("Plantilla guardada");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sendTest = async () => {
    if (!testEmail) return toast.warning("Introduce un email de prueba");
    setSending(true);
    try {
      await api.post("/admin/email/test", { to: testEmail, template: activeKey });
      toast.success(`Email de prueba enviado a ${testEmail}`);
      loadLogs();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  const triggerScan = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/admin/email/trigger-birthday-scan");
      toast.success(`Escaneo: ${data.birthday_emails} felicitación(es) · ${data.admin_notices} aviso(s) admin`);
      loadLogs();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setScanning(false); }
  };

  const tplInfo = TEMPLATES.find(t => t.key === activeKey);

  return (
    <div data-testid="admin-emails">
      <p className="overline mb-3">Automatización</p>
      <h1 className="font-heading text-4xl text-ivory mb-3">Emails de cumpleaños</h1>
      <p className="text-sm text-ivory/60 mb-8 max-w-2xl">
        Sistema automático que cada mañana detecta cumpleaños próximos, envía felicitaciones con cupón al cliente y un aviso interno para que prepares el regalo físico.
      </p>

      {/* Status banner */}
      <div className="bg-graphite border border-gold/40 p-5 mb-8 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <CheckCircle2 className="text-gold" size={20} />
          <div>
            <p className="text-ivory text-sm">Resend configurado · Scheduler activo (~9:00 CET diario)</p>
            <p className="text-xs text-ivory/50 mt-1">Remitente: <code className="text-gold">onboarding@resend.dev</code> (verifica tu dominio mhlumora.com en resend.com para usar hello@mhlumora.com)</p>
          </div>
        </div>
        <button
          onClick={triggerScan}
          disabled={scanning}
          className="btn-outline-gold !py-2 !px-4 flex items-center gap-2"
          data-testid="trigger-scan"
        >
          {scanning ? <RefreshCw size={14} className="animate-spin" /> : <Send size={14} />}
          Ejecutar escaneo ahora
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        {TEMPLATES.map(t => (
          <button
            key={t.key}
            onClick={() => setActiveKey(t.key)}
            data-testid={`tpl-tab-${t.key}`}
            className={`px-5 py-3 border text-xs uppercase tracking-[0.15em] ${
              activeKey === t.key ? "border-gold text-gold bg-graphite" : "border-smoke text-ivory/60 hover:text-ivory"
            }`}
          >
            {t.name}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Editor */}
        <div className="lg:col-span-2 bg-graphite border border-smoke p-6">
          <h2 className="font-heading text-2xl text-ivory mb-2">{tplInfo.name}</h2>
          <p className="text-sm text-ivory/60 mb-2">{tplInfo.description}</p>
          <p className="text-xs text-gold mb-6 font-mono">Variables disponibles: {tplInfo.variables}</p>

          {template && (
            <div className="space-y-4">
              <div>
                <label className="luxe-label">Asunto del email</label>
                <input
                  data-testid="tpl-subject"
                  value={template.subject || ""}
                  onChange={e => setTemplate({ ...template, subject: e.target.value })}
                  className="luxe-input"
                />
              </div>

              {tplInfo.hasDiscount && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="luxe-label">% Descuento</label>
                    <input
                      type="number"
                      value={template.discount_percent ?? 15}
                      onChange={e => setTemplate({ ...template, discount_percent: e.target.value })}
                      className="luxe-input"
                    />
                  </div>
                  <div>
                    <label className="luxe-label">Prefijo del cupón</label>
                    <input
                      value={template.discount_code_prefix || "BDAY"}
                      onChange={e => setTemplate({ ...template, discount_code_prefix: e.target.value })}
                      className="luxe-input"
                    />
                  </div>
                </div>
              )}

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="luxe-label !mb-0">Contenido HTML</label>
                  <button onClick={() => setShowPreview(p => !p)} className="text-xs text-gold uppercase tracking-wider flex items-center gap-1">
                    <Eye size={12} /> {showPreview ? "Ocultar" : "Mostrar"} preview
                  </button>
                </div>
                <textarea
                  data-testid="tpl-html"
                  value={template.html || ""}
                  onChange={e => setTemplate({ ...template, html: e.target.value })}
                  className="luxe-input border border-smoke px-3 py-3 min-h-[280px] font-mono text-xs"
                />
              </div>

              {showPreview && (
                <div className="border border-smoke">
                  <div className="bg-carbon px-4 py-2 text-xs text-ivory/50 uppercase tracking-wider">Preview · vista renderizada</div>
                  <iframe
                    title="email-preview"
                    srcDoc={(template.html || "").replace(/\{name\}/g, "Isabella")
                      .replace(/\{surname\}/g, "Moreno")
                      .replace(/\{code\}/g, "BDAYDEMO")
                      .replace(/\{discount\}/g, template.discount_percent ?? 15)
                      .replace(/\{email\}/g, "isabella@example.com")
                      .replace(/\{phone\}/g, "+34 600 000 000")
                      .replace(/\{address\}/g, "Calle Demo 1")
                      .replace(/\{postal_code\}/g, "28001")
                      .replace(/\{city\}/g, "Madrid")
                      .replace(/\{birthdate\}/g, "1990-01-01")
                      .replace(/\{days_until\}/g, "2")}
                    className="w-full h-96 bg-white"
                  />
                </div>
              )}

              <div className="flex gap-3 pt-4 border-t border-smoke">
                <button onClick={save} className="btn-gold flex items-center gap-2" data-testid="tpl-save">
                  <Save size={14} /> Guardar plantilla
                </button>
                <div className="flex gap-2 ml-auto">
                  <input
                    data-testid="test-email"
                    type="email"
                    placeholder="email@prueba.com"
                    value={testEmail}
                    onChange={e => setTestEmail(e.target.value)}
                    className="luxe-input border border-smoke px-3 py-2 min-w-[220px]"
                  />
                  <button onClick={sendTest} disabled={sending} className="btn-outline-gold flex items-center gap-2" data-testid="send-test">
                    {sending ? <RefreshCw size={14} className="animate-spin" /> : <Send size={14} />}
                    Enviar prueba
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Logs */}
        <div className="bg-graphite border border-smoke p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-xl text-ivory">Historial</h3>
            <button onClick={loadLogs} className="text-gold"><RefreshCw size={14} /></button>
          </div>
          <div className="space-y-3 max-h-[600px] overflow-y-auto">
            {logs.length === 0 && <p className="text-xs text-ivory/40">Sin envíos aún</p>}
            {logs.map((l, i) => (
              <div key={i} className="border-l-2 border-gold/30 pl-3 text-xs" data-testid={`log-${i}`}>
                <p className="text-ivory flex items-center gap-2">
                  {l.result?.sent ? <CheckCircle2 size={11} className="text-gold" /> : <AlertCircle size={11} className="text-rose" />}
                  <span className="uppercase tracking-wider text-[10px]">{l.type}</span>
                </p>
                <p className="text-ivory/60 mt-1">{l.to}</p>
                {l.coupon_code && <p className="text-gold mt-1 font-mono">{l.coupon_code}</p>}
                <p className="text-ivory/30 mt-1">{new Date(l.sent_at).toLocaleString("es-ES")}</p>
                {!l.result?.sent && <p className="text-rose mt-1">{l.result?.reason}</p>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
